import {
    Bot20Regular,
    CheckmarkCircle20Regular,
    ChevronDown20Regular,
    ChevronRight20Regular,
    Circle20Regular,
    DismissCircle20Regular,
    Person20Regular,
    ThumbDislike20Regular,
    ThumbLike20Regular,
} from "@fluentui/react-icons";
import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import Markdown from "markdown-to-jsx";
import Modal from "../Modal";
import { rateResponse } from "../../services/AssistantService";
import { Spinner } from "@fluentui/react-components";
import { documentReferences } from "./DocumentReferences";
import { machineReferences } from "./MachineReferences";
import { PoiReferences } from "./PoiReferences";
import { ChatProgress, ChecklistItem } from "../../Types/Chat/ChatProgress";

interface ChatBubbleProps {
    id: string;
    message: string;
    role: string;
    dataSources?: { name: string, data: string, url: string | undefined }[];
    animate: boolean;
    elements: ReactNode[];
    canBeRated: boolean;
    sessionId: number;
    setCanBeRated: (id: string, canBeRated: boolean) => void;
    progress?: ChatProgress;
}

const checklistIcon = (item: ChecklistItem) => {
    switch (item.state) {
        case "Completed":
            return <CheckmarkCircle20Regular className="text-[#15803D] flex-shrink-0" />;
        case "InProgress":
            return <Spinner size="tiny" className="flex-shrink-0" />;
        case "Failed":
            return <DismissCircle20Regular className="text-[#B91C1C] flex-shrink-0" />;
        default:
            return <Circle20Regular className="flex-shrink-0" />;
    }
};

// The assistant bubble is light gray in both themes, so the markdown colors need no dark: variants.

function MarkdownLink(props: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
    return (
        <a
            {...props}
            target="_blank"
            rel="noopener noreferrer"
        />
    );
}

// Code inside a <pre> is a code block and takes its colors from the block, any other code is inline.
const CodeBlockContext = createContext(false);

function MarkdownCode(props: React.HTMLAttributes<HTMLElement>) {
    const { children, className, ...rest } = props;
    const isBlockCode = useContext(CodeBlockContext);

    return (
        <code
            {...rest}
            className={isBlockCode ? className : "rounded bg-primary-light px-1.5 py-0.5 font-mono text-[0.95em] text-[#BE123C]"}
        >
            {children}
        </code>
    );
}

function MarkdownPre(props: React.HTMLAttributes<HTMLPreElement>) {
    return (
        <CodeBlockContext.Provider value={true}>
            <pre {...props} className="mb-3 overflow-x-auto rounded-md bg-oi-dark-background p-3 font-mono text-sm leading-6 text-primary-light last:mb-0" />
        </CodeBlockContext.Provider>
    );
}

function MarkdownTable(props: React.TableHTMLAttributes<HTMLTableElement>) {
    // The page renders in quirks mode, where tables don't inherit the text color and fall back to the
    // color scheme's default (white in dark mode), so the color is inherited explicitly.
    return (
        <div className="mb-3 overflow-x-auto last:mb-0">
            <table {...props} className="min-w-full border-collapse border border-primary-dark/20 text-left text-sm [color:inherit]" />
        </div>
    );
}

const renderMarkdown = (message: string, isAssistant: boolean) => (
    <Markdown
        options={{
            disableParsingRawHTML: true,
            forceBlock: true,
            overrides: {
                h1: { props: { className: "mb-3 text-2xl font-bold last:mb-0" } },
                h2: { props: { className: "mb-3 text-xl font-bold last:mb-0" } },
                h3: { props: { className: "mb-3 text-lg font-semibold last:mb-0" } },
                h4: { props: { className: "mb-3 text-base font-semibold last:mb-0" } },
                h5: { props: { className: "mb-3 text-sm font-semibold uppercase tracking-wide last:mb-0" } },
                h6: { props: { className: "mb-3 text-sm font-medium uppercase tracking-wide opacity-80 last:mb-0" } },
                p: { props: { className: "mb-3 last:mb-0 leading-6" } },
                // Links in the user's blue bubble keep the bubble's white text.
                a: {
                    component: MarkdownLink,
                    props: {
                        className: isAssistant
                            ? "text-chat-link underline underline-offset-2 break-all hover:text-chat-link/80"
                            : "underline underline-offset-2 break-all",
                    },
                },
                ul: { props: { className: "mb-3 list-disc pl-5 space-y-1 last:mb-0" } },
                ol: { props: { className: "mb-3 list-decimal pl-5 space-y-1 last:mb-0" } },
                li: { props: { className: "leading-6" } },
                blockquote: { props: { className: "mb-3 border-l-4 border-primary-dark/20 pl-4 italic opacity-80 last:mb-0" } },
                code: { component: MarkdownCode },
                pre: { component: MarkdownPre },
                table: { component: MarkdownTable },
                thead: { props: { className: "bg-primary-dark/5" } },
                th: { props: { className: "border border-primary-dark/20 px-3 py-2 font-semibold" } },
                td: { props: { className: "border border-primary-dark/20 px-3 py-2 align-top" } },
                hr: { props: { className: "my-4 border-t border-gray-300" } },
            },
        }}
    >
        {message}
    </Markdown>
);

export function ChatBubble(props: ChatBubbleProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [canBeRated, setCanBeRated] = useState(props.canBeRated);
    const [wasRated, setWasRated] = useState(false);
    const [isChecklistOpen, setIsChecklistOpen] = useState(false);
    const openModal = () => setIsModalOpen(true);
    const closeModal = () => setIsModalOpen(false);

    useEffect(() => {
        setCanBeRated(props.canBeRated);
    }, [props.canBeRated]);

    useEffect(() => {
        setWasRated(false);
    }, [props.id]);

    useEffect(() => {
        setIsChecklistOpen(false);
    }, [props.progress?.queryId]);

    const rate = async (sessionId: number, rating: number, id: string, message: string) => {
        props.setCanBeRated(id, false);
        setWasRated(true);
        await rateResponse(sessionId, rating, id, message);
    };

    return (
        <div className={(props.role === "assistant" ? "bg-chat-bg-reciever" : "bg-chat-bg") + " rounded-lg block w-full shadow-md"}>
            <div className={(props.role === "assistant" ? "text-text-light" : "text-text-dark") + " p-2.5 break-words"}>
                <div className="flex flex-row">
                    <span className="pr-1">
                        {props.role === "assistant" ? <Bot20Regular /> : <Person20Regular />}
                    </span>
                    <div className="px-2 w-11/12" >
                        {props.progress ? (
                            <div aria-live="polite" aria-atomic="true">
                                <div className="flex items-center min-h-[24px]">
                                    <span className="status-text-shimmer">{props.progress.status || "Thinking..."}</span>
                                </div>
                                {props.progress.checklist.length > 0 && (
                                    <div className="mt-2">
                                        <button
                                            type="button"
                                            className="flex items-center gap-1 text-sm font-semibold hover:underline"
                                            aria-expanded={isChecklistOpen}
                                            aria-controls={`checklist-${props.progress.queryId}`}
                                            onClick={() => setIsChecklistOpen((isOpen) => !isOpen)}
                                        >
                                            {isChecklistOpen ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
                                            <span>
                                                {props.progress.checklist.filter((item) => item.state === "Completed").length}
                                                /{props.progress.checklist.length} tasks complete
                                            </span>
                                        </button>
                                        {isChecklistOpen && (
                                            <ul id={`checklist-${props.progress.queryId}`} className="mt-2 space-y-1.5">
                                                {props.progress.checklist.map((item) => (
                                                    <li
                                                        key={item.id}
                                                        className={`flex items-start gap-2 text-sm ${item.state === "Pending" ? "opacity-70" : ""}`}
                                                    >
                                                        {checklistIcon(item)}
                                                        <span>{item.label}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : props.message === "showSpinner" ? <Spinner /> : renderMarkdown(props.message, props.role === "assistant")}

                        {props.role === "assistant" && props.dataSources && props.dataSources.filter(source => source.url).length > 0 && (
                            <div>
                                <hr className="mt-3 mb-3 border-t border-primary-dark/20" />
                                <div>
                                    {props.dataSources.some(source => source.url?.startsWith("document://")) && documentReferences({
                                        documents: (props.dataSources || []).filter(source =>
                                            source.url?.startsWith("document://")
                                        )
                                    })}
                                    {props.dataSources.some(source => source.url?.startsWith("machine://")) && machineReferences({
                                        documents: (props.dataSources || []).filter(source =>
                                            source.url?.startsWith("machine://")
                                        )
                                    })}
                                    {props.dataSources.some(source => source.url?.startsWith("poi://")) && PoiReferences({
                                        documents: (props.dataSources || []).filter(source =>
                                            source.url?.startsWith("poi://")
                                        )
                                    })}
                                </div>
                            </div>
                        )}

                    </div>
                    <span className="pl-1 pt-0 -mb-1">
                        {props.dataSources && (
                            <div className="relative group text-text-light ">
                                <div onClick={openModal} className="cursor-pointer underline">
                                    Source
                                </div>
                                <Modal isOpen={isModalOpen} onClose={closeModal} title="Data Sources">
                                    This message was generated using the following data sources:
                                    {props.dataSources.map((source, index) =>
                                        <div key={index} className="text-xs mt-2 border-t border-primary-dark/20 dark:border-primary-light/20 pt-1">
                                            <span className="font-bold">{source.name}: </span>
                                            {(() => {
                                                try {
                                                    const parsedData = JSON.parse(source.data);
                                                    return (
                                                        <div className="pl-2 mt-1 bg-primary-dark/5 dark:bg-primary-light/5 p-1 rounded">
                                                            {Object.entries(parsedData)
                                                                .map(([key, value]) => {
                                                                    let displayValue;
                                                                    if (value === null) {
                                                                        displayValue = "null";
                                                                    } else if (Array.isArray(value)) {
                                                                        displayValue = value;
                                                                    } else if (typeof value === 'object') {
                                                                        displayValue = (
                                                                            <div className="pl-2">
                                                                                {Object.entries(value).map(([nestedKey, nestedVal]) => (
                                                                                    <div key={nestedKey} className="flex">
                                                                                        <span className="font-semibold opacity-80 min-w-[120px]">{nestedKey}:</span>
                                                                                        <span>
                                                                                            {typeof nestedVal === 'object' && nestedVal !== null
                                                                                                ? '(Object)'
                                                                                                : String(nestedVal)}
                                                                                        </span>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        );
                                                                    } else {
                                                                        displayValue = String(value);
                                                                    }

                                                                    return (
                                                                        <div key={key} className="flex mb-2">
                                                                            <span className="font-semibold min-w-[80px]">{key}:</span>
                                                                            <span>{displayValue}</span>
                                                                        </div>
                                                                    );
                                                                })}
                                                        </div>
                                                    );
                                                } catch (e) {
                                                    // If not valid JSON, return the raw data
                                                    return <div className="italic opacity-70">{source.data}</div>;
                                                }
                                            })()}
                                        </div>
                                    )}

                                </Modal>

                            </div>

                        )}


                    </span>

                </div>
                {canBeRated && (
                    <div className="flex justify-end -mt-3 space-x-2 pr-1">
                        <ThumbLike20Regular className="cursor-pointer" onClick={() => rate(props.sessionId, 1, props.id, props.message)} />
                        <ThumbDislike20Regular className="cursor-pointer" onClick={() => rate(props.sessionId, 0, props.id, props.message)} />
                    </div>
                )}
                {wasRated && (
                    <div className="flex justify-end -mt-3 space-x-2 pr-1 opacity-75 pt-2  md:-mr-44 md:text-text-light md:dark:text-text-dark md:pt-0">
                        Thanks for the feedback!
                    </div>
                )}
            </div>
        </div>
    );
}

